import React, { useState } from 'react';
import { Search, Filter, X } from 'lucide-react';

interface RepositoryFiltersProps {
  onFilterChange: (filters: FilterState) => void;
  availableTags?: string[];
  availableLanguages?: string[];
}

export interface FilterState {
  search: string;
  tags: string[];
  language: string;
  hasFeatures: boolean;
}

export function RepositoryFilters({
  onFilterChange,
  availableTags = [],
  availableLanguages = [],
}: RepositoryFiltersProps) {
  const [filters, setFilters] = useState<FilterState>({
    search: '',
    tags: [],
    language: '',
    hasFeatures: false,
  });

  const [showAdvanced, setShowAdvanced] = useState(false);

  const updateFilters = (updates: Partial<FilterState>) => {
    const newFilters = { ...filters, ...updates };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const clearFilters = () => {
    const emptyFilters: FilterState = {
      search: '',
      tags: [],
      language: '',
      hasFeatures: false,
    };
    setFilters(emptyFilters);
    onFilterChange(emptyFilters);
  };

  const hasActiveFilters =
    filters.search ||
    filters.tags.length > 0 ||
    filters.language ||
    filters.hasFeatures;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
      {/* Search Bar */}
      <div className="flex gap-2">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search repositories..."
            value={filters.search}
            onChange={(e) => updateFilters({ search: e.target.value })}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
        >
          <Filter className="h-5 w-5" />
          Filters
        </button>
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2 text-red-600"
          >
            <X className="h-5 w-5" />
            Clear
          </button>
        )}
      </div>

      {/* Advanced Filters */}
      {showAdvanced && (
        <div className="pt-4 border-t border-gray-200 space-y-4">
          {/* Language Filter */}
          {availableLanguages.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Language
              </label>
              <select
                value={filters.language}
                onChange={(e) => updateFilters({ language: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Languages</option>
                {availableLanguages.map((lang) => (
                  <option key={lang} value={lang}>
                    {lang}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Tags Filter */}
          {availableTags.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tags
              </label>
              <div className="flex flex-wrap gap-2">
                {availableTags.map((tag) => {
                  const isSelected = filters.tags.includes(tag);
                  return (
                    <button
                      key={tag}
                      onClick={() => {
                        const newTags = isSelected
                          ? filters.tags.filter((t) => t !== tag)
                          : [...filters.tags, tag];
                        updateFilters({ tags: newTags });
                      }}
                      className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                        isSelected
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {tag}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Has Features Filter */}
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.hasFeatures}
                onChange={(e) => updateFilters({ hasFeatures: e.target.checked })}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">
                Only repositories with installed features
              </span>
            </label>
          </div>
        </div>
      )}

      {/* Active Filters Summary */}
      {hasActiveFilters && (
        <div className="flex flex-wrap gap-2 pt-2">
          {filters.search && (
            <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">
              Search: {filters.search}
            </span>
          )}
          {filters.language && (
            <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">
              Language: {filters.language}
            </span>
          )}
          {filters.tags.map((tag) => (
            <span key={tag} className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">
              Tag: {tag}
            </span>
          ))}
          {filters.hasFeatures && (
            <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">
              Has Features
            </span>
          )}
        </div>
      )}
    </div>
  );
}
